<template>
    <report-section
        :id="id"
        title="Introduction"
        class="introduction"
    >
        <p>
            This report provides information on forest disturbance, and recovery for the location
            ({{ reportData.selectedWatershed.name }}) that you selected. Equivalent Clearcut Area (ECA)
            is an approximation of forest disturbance, at a point in time, that accounts for the cumulative
            impact of previous forest disturbance on the hydrology of a watershed<sup>
                <footnote-tooltip
                    :footnote-number="1"
                />
            </sup>. It is an indicator that takes into account the historical type and location of disturbance
            and the subsequent recovery of disturbed areas over time, and equates the historical disturbances
            to a hypothetical scenario where an equivalent amount of forest was recently clearcut. The ECA
            indicator was originally intended to be used as a measure of risk for peak flow runoff generation,
            but can also act as a proxy for the impacts of forest disturbance on hydrological processes such as snow
            accumulation and melt, evaporation, transpiration, canopy interception, and infiltration rates
            which lead to changes in the dynamics of water balances and surface/groundwater interactions<sup>
                <footnote-tooltip
                    :footnote-number="2"
                />
            </sup>. While ECA provides a point in time assessment of cumulative disturbance, often the specific
            impacts of disturbance in a watershed are dependent on other characteristics of the landscape, including
            slope, aspect, elevation and other attributes. This report provides the ability to explore
            disturbance within the watershed, in relation to these characteristics.
        </p>
        <p>
            An ECA of 100% means that the entire area has been disturbed, and no recovery has yet occurred.
            An ECA of 0% means that the watershed has not been disturbed at all. Please note that all values
            presented are estimates based on limited data and are subject to error, and should be used in
            combination with other factors and professional judgement, to assess conditions for any specific watershed.
        </p>
        <p>
            The data used for the analysis described in this report originates from a variety of sources
            which are described in the Data Sources section below. In general, the primary source of data
            which describes the location (and year) of disturbance has been generated from automated classification
            and analysis performed on decades of earth observation satellite data provided by White et al<sup>
                <footnote-tooltip
                    :footnote-number="3"
                />
            </sup> and Hansen et al<sup>
                <footnote-tooltip
                    :footnote-number="4"
                />
            </sup>. This data has been integrated with watershed fabric data produced by the Province of BC<sup>
                <footnote-tooltip
                    :footnote-number="5"
                />
            </sup> and post-disturbance recovery projections from the BC Watershed Assessment Procedures Guidebook<sup>
                <footnote-tooltip
                    :footnote-number="1"
                />
            </sup> based on tree species, and models developed by Lewis and Huggard<sup>
                <footnote-tooltip
                    :footnote-number="6"
                />
            </sup> quantifying the impact of
            Mountain Pine Beetle infestation. Road networks and long-term sources of human disturbance in an area
            of detected change are assumed to be a constant contributor to channel change and peak flows according
            to the BC Watershed Assessment Procedures Guidebook, and therefore are assumed to be constant sources of
            disturbance. Road networks were identified by using the BC Digital Road Atlas<sup>
                <footnote-tooltip
                    :footnote-number="7"
                />
            </sup> and the BCER Road Rights of Way<sup>
                <footnote-tooltip
                    :footnote-number="8"
                />
            </sup> datasets. Human disturbances were acquired from the Province of BC's Cumulative Effects
            Framework<sup>
                <footnote-tooltip
                    :footnote-number="9"
                />
            </sup>.
        </p>
    </report-section>
</template>

<script>
import sectionMixin from '@foundryspatial/report-section/mixin';

export default {
    name: 'IntroductionSection',

    mixins: [
        sectionMixin,
    ],
};
</script>

<style lang="scss">
</style>
